import { MouseEventHandler, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';

import { useOnceReveal } from '@common/hooks';
import { Typography } from '@common/styles';
import { PlayableWrap, ReportedReviewMessage } from '@domains/review-list-common';

export interface SummaryCardProps {
  contents: string;
  thumbnail?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onImpression?: () => void;
  isReported: boolean;
  type: 'video' | 'photo';
}
export const SummaryCard = ({ type, onClick, contents, thumbnail, onImpression, isReported }: SummaryCardProps) => {
  const { targetRef: imageRef, isRevealed } = useOnceReveal({ rootMargin: '150px' });
  const [showPlaceHolder, setShowPlaceHolder] = useState(true);
  const start_time = useRef(Date.now());

  const { ref } = useInView({
    onChange(inView) {
      if (inView) {
        start_time.current = Date.now();
      } else {
        const end_time = Date.now();
        const duration = end_time - start_time.current;
        if (duration >= 300) {
          onImpression?.();
        }
      }
    },
    skip: typeof onImpression === 'undefined',
    threshold: 0.8,
  });

  useEffect(() => {
    if (isRevealed) {
      setShowPlaceHolder(false);
    }
  }, [isRevealed]);

  if (isReported) {
    return (
      <ItemCard ref={ref}>
        <ReportedReviewMessage />
      </ItemCard>
    );
  }

  return (
    <ItemCard
      ref={(ele) => {
        ref(ele);
        imageRef(ele);
      }}
      onClick={onClick}
    >
      <ContentBox>
        {thumbnail && !showPlaceHolder && (
          <Thumbnail src={thumbnail} alt='review-image' onError={() => setShowPlaceHolder(true)} />
        )}
        {type === 'video' && <PlayableWrap buttonSize={24} margin={6} />}
        {showPlaceHolder && <ImagePlaceHolder />}
        <ContentText className={Typography.BODY_14_REGULAR}>{contents}</ContentText>
      </ContentBox>
    </ItemCard>
  );
};

const ItemCard = styled.div`
  background-color: ${vars.color.gray20};
  width: 80vw;
  height: 80px;

  @media screen and (min-width: 720px) {
    max-width: 300px;
  }
`;

const ContentBox = styled.div`
  display: flex;
`;

const ContentText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  color: ${vars.color.textSecondary};
  margin: 10px 16px;
  line-height: 20px;
`;

const Thumbnail = styled.img`
  aspect-ratio: 1;
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  object-fit: cover;
  object-position: center;
`;

const ImagePlaceHolder = styled.div`
  min-width: 56px;
  width: 56px;
  height: 56px;
  margin-right: 8px;
  background-color: ${vars.color.gray30};
`;
